import React, {
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  withStyles
} from '@material-ui/core';
import flowRight from 'lodash.flowright';
import { connect } from 'react-redux';
import Select from 'react-select';

import CustomDialogTitle from '../../../partials/customDialogTitle/CustomDialogTitle';
import DraggableDialog from '../../../partials/DraggableDialog';
import styles, { customSubSelectStyles } from '../styles/changeSubscription';
import { translate } from '../../../i18n/I18nProvider';
import Optional from '../../../utils/optional';
import DataRow from '../../../partials/Datarow/DataRow';
import PrimaryButton from '../../../partials/customButtons/PrimaryButton';
import SecondaryButton from '../../../partials/customButtons/SecondaryButton';
import { actions as reduxActions } from '../reducers';
import getProducts, { mapPrice } from '../actions/getProducts';
import {
  doesArrayHasLength,
  isArray,
  isArrayEmpty,
  isDefined,
  isUndefined
} from '../../../utils/isDefined';
import { reactSelectDefaultStyles } from '../../../consts/reactSelectDefaultStyles';
import moment from 'moment';
import Summary from './Summary';
import changeSubscription from '../actions/changeSubscription';
import getSubscriptionDetails from '../actions/getSubscriptionDetails';
import Problems from '../components/Problems';
import CancelMigration from './CancelMigration';
import cancelMigration from '../actions/cancelMigration';

const ChangeSubscription = ({
  children,
  classes,
  subscription,
  clientId,
  lang,
  isMultiedit = false,
  selectedRows = [],
  openSummary,
  selectedAccount,
  setRefresh,
  fromIoT,
  skuPrefix,
  clientName,
  closeMenu = () => {}
}) => {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [changeDate, setChangeDate] = useState('');
  const [problems, setProblems] = useState(void 0);
  const [migration, setMigration] = useState(void 0);

  useEffect(() => {
    if (!open) return;

    closeMenu();
    const today = moment()
      .add(1, 'M')
      .format('x');
    setChangeDate(Number(today));
    if (
      (isMultiedit && isDefined(selectedAccount)) ||
      (!isMultiedit && isDefined(subscription?.accountReference))
    ) {
      (async function() {
        if (!isMultiedit) {
          const response = await getSubscriptionDetails({
            setIsLoading,
            clientId,
            externalReference: subscription.externalReference,
            accountReference: subscription.accountReference
          });

          if (isDefined(response?.subProblems)) {
            setProblems(response.subProblems);
          }

          if (isDefined(response?.subMigration)) {
            setMigration(response.subMigration);
          }
        }
        await getProducts({
          setProducts,
          setIsLoading,
          accountReference: isMultiedit
            ? selectedAccount?.externalReference
            : subscription?.accountReference,
          clientId,
          fromIoT,
          skuPrefix,
          subscription: isMultiedit ? {} : subscription
        });
      })();
    }

    // eslint-disable-next-line
  }, [
    selectedAccount,
    clientId,
    open,
    subscription,
    isMultiedit,
    fromIoT,
    skuPrefix
  ]);

  const onOpenDialog = () => {
    setOpen(true);
    setProducts([]);
    setIsLoading(false);
  };

  const onCloseDialog = useCallback(
    (shouldRefresh = false) => {
      const isRefreshRequested =
        typeof shouldRefresh === 'boolean' && shouldRefresh;
      if (fromIoT || isRefreshRequested) {
        setRefresh(true);
      }
      setOpen(false);
      setProducts([]);
      setProduct(null);
      setIsLoading(false);
      setChangeDate('');
      setProblems(void 0);
      setMigration(void 0);
    },
    [setOpen, setRefresh, fromIoT]
  );

  const customClasses = {
    root: classes.datarowRoot,
    label: classes.dataRowLabel,
    value: classes.datarowValue
  };

  const onChangeClick = useCallback(async () => {
    const { requestCompleted, summaryInfo } = await changeSubscription({
      clientId,
      subscription,
      isMultiedit,
      selectedRows,
      setIsLoading,
      newProduct: product,
      changeDate,
      clientName
    });

    if (requestCompleted) {
      const subs = Object.keys(summaryInfo);

      const errors = subs.filter(sub => {
        const statuses = summaryInfo[sub].status;

        if (!isArray(statuses)) {
          return false;
        }

        const filteredStatuses = statuses.find(el => el.isError === true);

        return isDefined(filteredStatuses);
      });

      openSummary({
        summaryInfo,
        fromChange: true,
        isSummaryError: doesArrayHasLength(errors)
      });

      if (isArrayEmpty(errors)) {
        onCloseDialog(true);
      }
    }
  }, [
    clientId,
    subscription,
    isMultiedit,
    selectedRows,
    product,
    changeDate,
    openSummary,
    onCloseDialog,
    setIsLoading
  ]);

  const onCancelMigrationClick = useCallback(
    async () => {
      setIsLoading(true);
      const isSuccess = await cancelMigration({
        clientId,
        externalReference: subscription?.externalReference,
        setIsLoading
      });

      if (isSuccess) {
        setMigration(void 0);
        setRefresh(true);
      }

      return setIsLoading(false);
    },
    // eslint-disable-next-line
    [clientId, subscription?.externalReference]
  );

  const handleChange = selected => {
    setProduct(selected);
  };

  const productsOptions = useMemo(() => {
    const options = Optional(
      isMultiedit
        ? products[selectedAccount?.externalReference]
        : products[subscription?.accountReference]
    ).or([]);

    return isMultiedit
      ? options
      : options.filter(option => option.sku !== subscription.sku);
  }, [selectedAccount, products, subscription, isMultiedit]);

  const currentPrice = useMemo(() => {
    const options = Optional(
      isMultiedit
        ? products[selectedAccount?.externalReference]
        : products[subscription?.accountReference]
    ).or([]);

    const currentProduct = options.find(
      option => option.sku === subscription?.sku
    );
    if (isUndefined(currentProduct)) {
      return '';
    }

    return mapPrice(currentProduct);
  }, [subscription, isMultiedit, products, selectedAccount]);

  return (
    <>
      {cloneElement(children, { onClick: onOpenDialog })}
      <Dialog
        maxWidth="lg"
        classes={{
          paper: classes.root
        }}
        PaperComponent={DraggableDialog}
        open={open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            onCloseDialog();
          }
        }}
      >
        <CustomDialogTitle onCloseClick={onCloseDialog}>
          {translate(
            isMultiedit
              ? 'BUTTON.MULTIEDIT'
              : 'SUBSCRIPTIONS.CHANGE_SUBSCRIPTION'
          )}
        </CustomDialogTitle>
        {isLoading && (
          <div className={classes.loaderOverlay}>
            <CircularProgress />
          </div>
        )}
        <DialogContent className={classes.dialogContent}>
          {isLoading && (
            <div className={classes.loaderOverlay}>
              <CircularProgress />
            </div>
          )}
          <div className={classes.editContainer}>
            {!isMultiedit ? (
              <>
                <DataRow
                  label={translate('PRODUCT')}
                  value={Optional(subscription?.translations?.sku?.[lang]).or(
                    Optional(subscription?.translations?.sku?.default).or('')
                  )}
                  customClasses={customClasses}
                />
                <DataRow
                  label={translate('CURRENT_PRICE')}
                  value={`${currentPrice} DKK`}
                  customClasses={customClasses}
                />
                <DataRow
                  label={translate('NAME')}
                  value={Optional(subscription?.name).or('')}
                  customClasses={customClasses}
                />
              </>
            ) : (
              <DataRow
                label={translate('MENU.SUBSCRIPTIONS')}
                value={selectedRows.map(el => el.name).join(', ')}
                customClasses={customClasses}
              />
            )}
            {isDefined(problems) && <Problems problems={problems} />}
            {isDefined(migration) && (
              <CancelMigration
                migration={migration}
                productsOptions={productsOptions}
                lang={lang}
              />
            )}
            {isUndefined(problems) && isUndefined(migration) && (
              <>
                <div className={classes.selectContainer}>
                  {productsOptions.length > 1 ? (
                    <>
                      <div className={classes.label}>
                        {translate('REQUEST_NEW_USER.SUBSCRIPTION_STEP')}
                      </div>
                      <Select
                        options={productsOptions}
                        styles={reactSelectDefaultStyles}
                        customStyles={customSubSelectStyles}
                        value={product}
                        onChange={handleChange}
                        isDisabled={
                          isUndefined(selectedAccount) ||
                          isArrayEmpty(productsOptions)
                        }
                        maxMenuHeight={120}
                      />
                    </>
                  ) : (
                    <div className={classes.noOptionsMessage}>
                      {translate('SUBSCRIPTIONS.NO_OTHER_PRODUCTS_AVAILABLE')}
                      <br />
                      {translate(
                        'SUBSCRIPTIONS.CONTACT_CUSTOMER_CARE_FOR_ACCESS'
                      )}
                    </div>
                  )}
                </div>
                <div className={classes.info}>
                  {translate('SUBSCRIPTIONS.SUMMARY_TEXT_2')}
                </div>
              </>
            )}
          </div>
        </DialogContent>
        <div className={classes.noteContainer}>
          <div className={classes.feeNote}>
            {translate('SUBSCRIPTIONS.DOWNGRADE_FEE_NOTE')}
          </div>
        </div>
        <DialogActions className={classes.footer}>
          <SecondaryButton onClick={onCloseDialog}>
            {translate('BUTTON.CANCEL')}
          </SecondaryButton>
          {isUndefined(problems) &&
            (isUndefined(migration) || isMultiedit ? (
              <PrimaryButton
                onClick={onChangeClick}
                disabled={
                  (!isMultiedit && subscription?.sku === product?.value) ||
                  isUndefined(product) ||
                  product?.value === ''
                }
              >
                {translate('SUBSCRIPTIONS.CHANGE_SUBSCRIPTION')}
              </PrimaryButton>
            ) : (
              <PrimaryButton onClick={onCancelMigrationClick}>
                {translate('SUBSCRIPTION.CANCEL_CHANGE')}
              </PrimaryButton>
            ))}
        </DialogActions>
      </Dialog>
      <Summary />
    </>
  );
};

const mapStatesToProps = ({ selectClient, auth, iotProducts }) => {
  return {
    clientId: Optional(selectClient.selectedClient?.id).or(void 0),
    clientName: Optional(selectClient.selectedClient?.name).or(''),
    lang: auth.lang,
    selectedAccount: iotProducts.selectedAccount
  };
};

const mapDispatchToProps = {
  openSummary: reduxActions.openSummary
};

export default flowRight(
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(ChangeSubscription);
